import { FormEvent, useState } from "react";
import { useParams } from "react-router-dom";
import { submitPublicContactAsync } from "../../api/PublicApi";
import {
  PublicContactRequest,
  PublicContactSource,
} from "../../models/PublicBookingModels";
import PublicCaptcha from "./PublicCaptcha";

interface ContactFormValues {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface ContactFieldErrors {
  fullName?: string;
  email?: string;
  contact?: string;
  message?: string;
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const PublicContactForm = () => {
  const { slug } = useParams<{ slug: string }>();

  const [form, setForm] = useState<ContactFormValues>({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<ContactFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaError, setCaptchaError] = useState("");

  const setField = (key: keyof ContactFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined, contact: undefined }));
  };

  const validate = (): ContactFieldErrors => {
    const next: ContactFieldErrors = {};
    if (!form.fullName.trim()) next.fullName = "Please enter your name.";
    if (!form.message.trim()) next.message = "Please enter a message.";

    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!email && !phone) {
      next.contact =
        "Please provide an email or a phone number so we can reach you.";
    }
    if (email && !EMAIL_RE.test(email)) {
      next.email = "Please enter a valid email address.";
    }
    return next;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const nextErrors = validate();
    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }
    if (!captchaValid) {
      setCaptchaError("Incorrect security answer. Please try again.");
      return;
    }
    if (!slug) return;

    setCaptchaError("");
    setServerError("");
    setSubmitting(true);
    try {
      const request: PublicContactRequest = {
        fullName: form.fullName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        source: PublicContactSource.Website,
      };
      const res = await submitPublicContactAsync(slug, request);
      if (res.success) {
        setSubmitted(true);
      } else {
        setServerError(res.message || "Could not send your message.");
      }
    } catch {
      setServerError("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psite-feedback">
      <h2>Please reach out to us if you have any feedback or queries.</h2>

      {submitted ? (
        <p className="psite-feedback__success">
          Thank you, {form.fullName || "friend"}! Your message has been
          received. We will get back to you soon.
        </p>
      ) : (
        <form className="psite-form" onSubmit={handleSubmit} noValidate>
          <div className="psite-form__row">
            <div className="psite-form__field">
              <input
                type="text"
                placeholder="Your name"
                aria-label="Your name"
                aria-required="true"
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
              />
              {errors.fullName && (
                <p className="psite-feedback__error">{errors.fullName}</p>
              )}
            </div>
            <div className="psite-form__field">
              <input
                type="email"
                placeholder="Email"
                aria-label="Email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
              {errors.email && (
                <p className="psite-feedback__error">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="psite-form__row">
            <div className="psite-form__field">
              <input
                type="tel"
                placeholder="Phone"
                aria-label="Phone"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
              />
            </div>
            <div className="psite-form__field">
              <input
                type="text"
                placeholder="Subject"
                aria-label="Subject"
                value={form.subject}
                onChange={(e) => setField("subject", e.target.value)}
              />
            </div>
          </div>

          {errors.contact && (
            <p className="psite-feedback__error">{errors.contact}</p>
          )}

          <div className="psite-form__field">
            <textarea
              placeholder="Message"
              aria-label="Message"
              aria-required="true"
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
            />
            {errors.message && (
              <p className="psite-feedback__error">{errors.message}</p>
            )}
          </div>

          <PublicCaptcha
            onValidChange={setCaptchaValid}
            onReset={() => setCaptchaError("")}
          />
          {(captchaError || serverError) && (
            <p className="psite-feedback__error">
              {captchaError || serverError}
            </p>
          )}
          <button
            type="submit"
            className="psite-btn psite-btn--primary"
            disabled={submitting}
          >
            {submitting ? "Sending..." : "Send feedback"}
          </button>
        </form>
      )}
    </div>
  );
};

export default PublicContactForm;
